import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Dimensions, ScrollView, TextStyle, View, ViewStyle } from 'react-native'
import { save, setPageTitle, useStores } from '@trivie/core'
import {
  Avatar,
  Button,
  color,
  iconFlashTrivie,
  QuizResultPanels,
  ReviewPanel,
  Scoreboard,
  SvgIcon,
  Text,
} from '@trivie/ui-mobile'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { Dialog } from '@mui/material'
import { useMediaQuery } from '@mui/material'
import { sortBy } from 'lodash'

export const ResultsScreen: React.FunctionComponent<any> = observer((props) => {
  const { rootCache, uiStore, gameStore, libraryStore, quizStore, userStore } = useStores()
  const { quiz } = gameStore
  const {
    scoreboardOfQuiz,
    scoreboardRecallOfQuiz,
    getScoreboardForQuiz,
    getScoreboardRecallForQuiz,
  } = quizStore
  const { getReview, review } = gameStore

  const { selectedTopic } = libraryStore
  const { user, checkNotifications, levelAndPoints, getUserLevelAndPoints, enterpriseTier } =
    userStore

  const scrollRef = useRef<any>(null)

  const [showResults, setShowResults] = useState(true)
  const [quizType, setQuizType] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [score, setScore] = useState(0)
  const [showScoreboard, setShowScoreboard] = useState(false)
  const [showQuizError, setShowQuizError] = useState(false)

  useEffect(() => {
    if (quiz?.tutorial) {
      save('show_tutorial', false)
      quizStore.setShowTutorial(false)
    }

    if (review) {
      if (review?.is_reinforcement) {
        setQuizType('reinforcement')
      } else if (review?.is_assessment) {
        if (review?.post_test_score === null) {
          setQuizType('pre-test')
        } else {
          setQuizType('post-test')
        }
      } else {
        setQuizType('regular')
      }
      setScore(review?.total_correct / review?.total_questions)
    }
  }, [review])

  const history = useHistory()
  const { params }: any = useRouteMatch()
  const topic_id = params?.topicID
  const result_id = quiz?.result.id || params?.resultID
  const { from } = history.location.state as any

  useEffect(() => {
    setPageTitle(`Results - ${process.env.REACT_APP_SITE_TITLE}`)

    rootCache.invalidateGroup('content')

    quizStore.setLoadingPostQuiz(true)

    if (!quizStore.loadingResults) {
      if (quiz?.tutorial) {
        quizStore.setLoadingPostQuiz(false)
        const totalCorrect = quiz.tutorialAnswers?.reduce(
          (acc: number, curr: any) => (curr.correct ? acc + 1 : acc),
          0,
        )
        quizStore.setResult({
          total_correct: totalCorrect,
          total_questions: quiz.tutorialAnswers.length,
        })
      } else if (user) {
        if (result_id) {
          getReview(result_id, user.id, topic_id)
            .then((resp) => {
              getUserLevelAndPoints(
                user.id,
                '1970-01-01 04:00:00',
                DateTime.local().plus({ days: 1 }).toFormat('yyyy-MM-dd HH:mm'),
              )

              if (user) {
                checkNotifications(user.id)
              }

              if (
                from !== 'quiz-detail' &&
                from !== 'profile' &&
                quizType !== 'post-test' &&
                quizType !== 'pre-test'
              ) {
                Promise.all([
                  getScoreboardForQuiz(result_id, user.id, topic_id).then((d: any) => {
                    if (d?.error) {
                      setShowQuizError(true)
                    }
                  }),
                  getScoreboardRecallForQuiz(result_id, user.id, topic_id).then((d: any) => {
                    if (d?.error) {
                      setShowQuizError(true)
                    }
                  }),
                ]).then((d) => {
                  setIsLoading(false)
                })
              }
              quizStore.setLoadingPostQuiz(false)
            })
            .catch((e) => {
              // handle no result
              // either due to not finishing the quiz or less likely: timeout
              setShowQuizError(true)
            })
        }
      }
    }
  }, [quizStore.loadingResults])

  const { t } = useTranslation()

  const getScoreTitle = () => {
    if (review?.total_questions) {
      const perc = (review?.total_correct || 0) / review.total_questions
      if (perc < 0.7) {
        return `${t('Quiz.niceTry')}`
      } else if (perc > 0.7 && perc <= 0.8) {
        return `${t('Quiz.goodJob')}`
      } else if (perc >= 0.81 && perc <= 0.99) {
        return `${t('Quiz.greatWork')}`
      } else if (perc === 1) {
        return `${t('Quiz.perfect')}`
      }
    }
  }

  const exit = () => {
    if (userStore.sharedQuiz) {
      userStore.setSharedQuiz(null)
      history.replace('/library')
    } else if (quiz?.reinforcement) {
      history.replace('/play')
    } else {
      history.goBack()
    }
  }

  const handleNext = () => {
    if (
      enterpriseTier &&
      (quizType === 'regular' || quizType === 'reinforcement') &&
      !quiz?.tutorial &&
      from !== 'quiz-history'
    ) {
      if (from !== 'quiz-detail' && from !== 'profile' && showResults) {
        setShowResults(false)
        setShowScoreboard(true)
      } else {
        exit()
      }
    } else {
      exit()
    }
  }

  const answers = quiz?.tutorial ? quiz?.tutorialAnswers : sortBy(review?.items, ['order'])
  const isMobile = !useMediaQuery('(min-width:800px)')

  return (
    <View style={{ ...FULL, backgroundColor: uiStore.branding.quiz_background, paddingTop: 36 }}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{
          paddingHorizontal: 20,
          width: '100%',
          maxWidth: 650,
          alignSelf: 'center',
        }}
      >
        <Avatar
          style={AVATAR}
          source={user?.photo_url}
          name={`${user?.first_name} ${user?.last_name}`}
          size="medium60"
        />
        {quizStore.loadingPostQuiz ? (
          <View style={{ justifyContent: 'center', height: Dimensions.get('screen').height / 2 }}>
            <ActivityIndicator size="small" />
          </View>
        ) : (
          showResults && (
            <>
              <Text
                style={{ color: uiStore.branding.text_color, marginBottom: 30 }}
                variant="headline"
                align="center"
                tx={getScoreTitle()}
              />
              <Text
                style={{ color: uiStore.branding.text_color, marginBottom: 8 }}
                variant="body1Medium"
                align="center"
                tx={
                  quizType === 'pre-test'
                    ? 'Scoreboard.preTestResults'
                    : quizType === 'post-test'
                      ? 'Scoreboard.postTestResults'
                      : 'Quiz.quizResults'
                }
              />
              <View
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
              >
                {quizType === 'reinforcement' && (
                  <View style={{ bottom: 10 }}>
                    <SvgIcon size={24} src={iconFlashTrivie} />
                  </View>
                )}
                {review && (
                  <Text
                    style={{ ...TITLE, color: uiStore.branding.text_color }}
                    variant="labelMedium"
                    text={
                      quizType === 'pre-test'
                        ? review.title.replace('Pre-Test', '')
                        : quizType === 'post-test'
                          ? review.title.replace('Post-Test', '')
                          : review.title
                    }
                    tx={quizType === 'reinforcement' ? 'Quiz.reinforce' : ''}
                  />
                )}
              </View>
              <QuizResultPanels
                enterpriseTier={enterpriseTier}
                result={quiz?.tutorial ? quizStore.result : review}
                quiz={quiz}
                score={score}
                branding={uiStore.branding}
                quizType={quizType}
              />
              {!enterpriseTier && !isLoading ? (
                <Scoreboard
                  branding={uiStore.branding}
                  enterpriseTier={enterpriseTier}
                  attempts={selectedTopic?.quizzes_completed}
                  loading={isLoading}
                  quizType={'freeTier'}
                  levelsEnabled={user?.workspace_settings?.user_levels_enabled}
                  levelAndPoints={levelAndPoints}
                  result={review}
                  scoreboardOfQuiz={scoreboardOfQuiz}
                  scoreboardRecallOfQuiz={scoreboardRecallOfQuiz}
                  background={uiStore.branding.quiz_background}
                />
              ) : null}
              <View
                style={{ height: 1, backgroundColor: 'rgba(127,127,127,.5)', marginBottom: 25 }}
              />
              <Text
                style={{
                  color: uiStore.branding.text_color,
                  textAlign: 'center',
                  marginBottom: 20,
                }}
                variant="titleEmphasized"
                tx="Quiz.reviewQuestions"
              />
              {answers?.map((item: any) => {
                return (
                  <ReviewPanel
                    onClickInfoCard={(id) => {
                      quiz?.setActiveItem(undefined)
                      history.push(`/library/cards/${id}`)
                    }}
                    branding={uiStore.branding}
                    item={item}
                  />
                )
              })}
            </>
          )
        )}
        {showScoreboard ? (
          isLoading ? (
            <View
              style={{
                justifyContent: 'center',
                height: Dimensions.get('screen').height / 2,
                right: window.innerWidth,
              }}
            >
              <ActivityIndicator size="small" />
            </View>
          ) : (
            <Scoreboard
              branding={uiStore.branding}
              enterpriseTier={enterpriseTier}
              attempts={selectedTopic?.quizzes_completed}
              loading={isLoading}
              quizType={quizType}
              levelsEnabled={user?.workspace_settings?.user_levels_enabled}
              levelAndPoints={levelAndPoints}
              result={review}
              scoreboardOfQuiz={scoreboardOfQuiz}
              scoreboardRecallOfQuiz={scoreboardRecallOfQuiz}
              background={uiStore.branding.quiz_background}
            />
          )
        ) : null}
        {showQuizError && (
          <Dialog aria-labelledby="confirmation-dialog-title" open={showQuizError}>
            <View style={{ ...MESSAGE_DIALOG, width: isMobile ? '100%' : 450 }}>
              <div style={{ flex: 1 }} />
              <div style={{ flex: 1 }}>
                <Text
                  style={{ textAlign: 'center' }}
                  variant="titleEmphasized"
                  tx="Quiz.noResults"
                />
              </div>
              <Button
                accessibilityLabel="Close"
                style={{ marginTop: 20 }}
                tx="Common.ok"
                onPress={() => {
                  setShowQuizError(false)
                  exit()
                }}
              />
            </View>
          </Dialog>
        )}
      </ScrollView>
      <div
        style={{ width: '100%', flexDirection: 'row', display: 'flex', justifyContent: 'center' }}
      >
        <Button
          variant="secondary"
          style={{
            ...NEXT_BUTTON,
            backgroundColor: uiStore.branding.accent_color,
            width: isMobile ? 336 : 500,
          }}
          textStyle={{ color: color.white }}
          tx="Common.next"
          onPress={handleNext}
        />
      </div>
    </View>
  )
})

const NEXT_BUTTON: ViewStyle = {
  margin: 20,
  alignSelf: 'center',
}
const AVATAR: ViewStyle = {
  marginBottom: 13,
  alignSelf: 'center',
  borderWidth: 3,
  borderRadius: 100,
  borderColor: '#F9F9F930',
}
const FULL: ViewStyle = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  flex: 1,
  zIndex: 999,
}
const TITLE: TextStyle = {
  alignSelf: 'center',
  color: color.white,
  textAlign: 'center',
  marginBottom: 25,
}
const MESSAGE_DIALOG: ViewStyle = {
  padding: 20,
  borderRadius: 20,
  backgroundColor: color.white,
  display: 'flex',
  justifyContent: 'space-evenly',
}
